import BaseModal from '@/components/BaseModal';
import ConditionBanner from '@/components/ConditionBanner';
import ExcludeRuleCondition from '@/components/ExcludeRuleCondition';
import LocationRuleCondition from '@/components/LocationRuleCondition';
import ModalItemCondition from '@/components/ModalItemCondition';
import ModalLocationCondition from '@/components/ModalLocationCondition';
import RegularText from '@/components/RegularText';
import { defaultDaySchedules, errorFieldTypeRuleConfig } from '@/constants';
import { AppliedItemTypes, BREAKPOINT, LocationConditionEnum } from '@/constants/enum';
import { checkConditionType } from '@/helpers';
import ruleSlice, { IRuleErrorField, ruleSettingsSelector } from '@/redux/slice/rule.slice';
import sessionSlice, { isShowBannerSelector } from '@/redux/slice/session.slice';
import { IConditionValue, IItemCondition, ILocationCondition } from '@/types/newSetup.type';
import { Button } from '@shopify/polaris';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import RuleDisplayTime from './components/RuleDisplayTime';

interface IItemToRemove {
  conditionType: 'itemConditions' | 'locationConditions';
  condition: IItemCondition['type'] | ILocationCondition['type'] | null;
}

export interface IDefaultOpenModalAddCondition {
  product: boolean;
  collection: boolean;
  vendor: boolean;
  inventoryLocations: boolean;
  productTags: boolean;
  skus: boolean;
  exception: boolean;
}

const DisplayConditions = () => {
  const dispatch = useDispatch();
  const state = useSelector(ruleSettingsSelector);

  const isMobile = useMediaQuery({ maxWidth: BREAKPOINT.SM });
  // const { currentPlan } = useUserPlan();
  const { isShowBannerConditionRule } = useSelector(isShowBannerSelector);

  const [isOpenModalLocationCondition, setIsOpenModalLocationCondition] = useState(false);
  const [isOpenModalItemCondition, setIsOpenModalItemCondition] = useState(false);
  const [isOpenModalConfirmRemoveCondition, setIsOpenModalConfirmRemoveCondition] = useState(false);
  const [trackRemove, setTrackRemove] = useState(false);
  const [selectedItemToRemove, setSelectedItemToRemove] = useState<IItemToRemove>({
    conditionType: 'itemConditions',
    condition: null,
  });

  // const [clickCount, setClickCount] = useState({
  //   itemConditions: 0,
  //   locationConditions: 0,
  // });

  // const [sendEventCustomerIo] = apiCaller.useSendEventCustomerIoMutation();

  const isInCaseInclude = useMemo(() => {
    return checkConditionType(state.itemConditions, AppliedItemTypes.Include);
  }, [state.itemConditions]);

  const isInCaseExclude = useMemo(() => {
    return checkConditionType(state.itemConditions, AppliedItemTypes.Exclude);
  }, [state.itemConditions]);

  const handleChangeCondition = useCallback(
    (conditionType: 'itemConditions' | 'locationConditions') =>
      (key: IItemCondition['type'] | ILocationCondition['type']) =>
      (value: IConditionValue[] | null) => {
        let conditions = [...state[conditionType]];
        if (value) {
          let oldCondition = conditions.find((item) => item.type === key);
          if (oldCondition) {
            oldCondition = { ...oldCondition, value };
            const indexOfOldCondition = conditions.findIndex((item) => item.type === key);
            conditions.splice(indexOfOldCondition, 1, oldCondition);
          } else {
            conditions.push({ type: key, value });
          }
        } else {
          conditions = conditions.filter((item) => item.type !== key);
          if (key === LocationConditionEnum.Country) {
            conditions = conditions.filter((item) => item.type !== LocationConditionEnum.State);
          }
        }
        dispatch(
          ruleSlice.actions.handleChange({ key: conditionType, value: conditions as IItemCondition[] | ILocationCondition[] }),
        );
      },
    [dispatch, state],
  );

  const handleSetError = useCallback(
    (field: IRuleErrorField, msg: string) => {
      dispatch(
        ruleSlice.actions.handleSetError({
          value: { field, msg },
        }),
      );
    },
    [dispatch],
  );

  const handleShowRemoveCondition =
    (conditionType: 'itemConditions' | 'locationConditions') =>
    (condition: IItemCondition['type'] | ILocationCondition['type']) => {
      setSelectedItemToRemove({ conditionType, condition });
      setIsOpenModalConfirmRemoveCondition(true);
    };

  const handleConfirmRemoveCondition = () => {
    const { conditionType, condition } = selectedItemToRemove;
    if (condition) {
      let field = condition as IRuleErrorField;

      const tempField = errorFieldTypeRuleConfig[condition as IItemCondition['type']] as IRuleErrorField;
      if (tempField) field = tempField;

      handleChangeCondition(conditionType)(condition)(null);
      handleSetError(field as IRuleErrorField, '');
      if (field === 'productMetafield') handleSetError('productMetafieldItem', '');
      if (field === 'variantMetafield') handleSetError('variantMetafieldItem', '');
      setTrackRemove((prev) => !prev);
      setIsOpenModalConfirmRemoveCondition(false);
    }
  };

  // useEffect(() => {
  //   if (currentPlan !== Plan.FREE) return;

  //   if (clickCount.itemConditions === 2) {
  //     setClickCount((prev) => ({ ...prev, itemConditions: prev.itemConditions + 1 }));
  //     sendEventCustomerIo({ event: '2nd_item_click' });
  //   }
  //   if (clickCount.locationConditions === 2) {
  //     setClickCount((prev) => ({ ...prev, locationConditions: prev.locationConditions + 1 }));
  //     sendEventCustomerIo({ event: '2nd_location_click' });
  //   }
  // }, [clickCount, currentPlan, sendEventCustomerIo]);

  return (
    <div className="d-flex flex-column gap-24">
      <ConditionBanner
        isVisible={isShowBannerConditionRule}
        isAllCondition={!state.itemConditions.length}
        onDismiss={() => dispatch(sessionSlice.actions.handleBanner({ key: 'isShowBannerConditionRule', value: false }))}
      />
      <ExcludeRuleCondition
        onAction={() => {
          setIsOpenModalItemCondition(true);
          // setClickCount((prev) => ({ ...prev, itemConditions: prev.itemConditions + 1 }));
        }}
        data={state.itemConditions}
        onChange={handleChangeCondition('itemConditions')}
        type={isInCaseInclude ? AppliedItemTypes.Include : isInCaseExclude ? AppliedItemTypes.Exclude : AppliedItemTypes.All}
        onRemove={handleShowRemoveCondition('itemConditions')}
        trackRemove={trackRemove}
        typeRelationOfItem={state.typeRelationOfItemCondition}
        isCountInventoryQuantityFromLocations={state.isCountInventoryQuantityFromLocations}
        handleChangeCountInventoryQuantityFromLocations={(value) => {
          dispatch(ruleSlice.actions.handleChange({ key: 'isCountInventoryQuantityFromLocations', value }));
        }}
      />
      <LocationRuleCondition
        onAction={() => {
          setIsOpenModalLocationCondition(true);
          // setClickCount((prev) => ({ ...prev, locationConditions: prev.locationConditions + 1 }));
        }}
        data={state.locationConditions}
        onChange={handleChangeCondition('locationConditions')}
        onRemove={handleShowRemoveCondition('locationConditions')}
      />
      <RuleDisplayTime
        data={{
          displayMode: state.displayMode,
          startDisplayDate: state.startDisplayDate,
          endDisplayDate: state.endDisplayDate,
          daySchedules: state.daySchedules && state.daySchedules.length ? state.daySchedules : defaultDaySchedules,
        }}
        onChange={(data) => {
          dispatch(ruleSlice.actions.setValues({ value: { ...state, ...data } }));
          dispatch(ruleSlice.actions.handleSetError({ value: { field: 'serverError', msg: '' } }));
        }}
      />
      <ModalLocationCondition
        isOpenModal={isOpenModalLocationCondition}
        setIsOpenModal={setIsOpenModalLocationCondition}
        onAdd={handleChangeCondition('locationConditions')}
        data={state.locationConditions}
      />
      <ModalItemCondition
        isOpenModal={isOpenModalItemCondition}
        setIsOpenModal={setIsOpenModalItemCondition}
        onAdd={handleChangeCondition('itemConditions')}
        data={state.itemConditions}
        type={isInCaseInclude ? AppliedItemTypes.Include : isInCaseExclude ? AppliedItemTypes.Exclude : AppliedItemTypes.All}
      />
      <BaseModal
        open={isOpenModalConfirmRemoveCondition}
        title={'Delete this display condition?'}
        onClose={() => setIsOpenModalConfirmRemoveCondition(false)}
        footer={
          <div className="content-right" style={{ width: isMobile ? 'calc(100vw - 3rem)' : '588px' }}>
            <Button onClick={() => setIsOpenModalConfirmRemoveCondition(false)}>Cancel</Button>
            <div className="ml-8">
              <Button tone="critical" variant="primary" onClick={handleConfirmRemoveCondition}>
                Delete
              </Button>
            </div>
          </div>
        }
      >
        <RegularText>This action cannot be undone</RegularText>
      </BaseModal>
    </div>
  );
};

export default DisplayConditions;
